import React, { useState } from "react";
import {
    faMoneyCheckDollar,
    faMoneyBillWave,
    faTriangleExclamation,
    faCalendarAlt,
    faArrowLeft,
    faEdit,
    faCheck,
    faTimes,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { capitalizeFirstLetter } from "../../Utils/helper";
import { toast } from "react-toastify";
import { updateHistory } from "../../Redux/Actions/BillingsActions";
import { useDispatch } from "react-redux";
import Loader from "../../Common/Loader";

const BillingHistory = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useDispatch()
    const { billingData } = location.state || {};

    const { id } = useParams()
    const [isEditing, setIsEditing] = useState(false);
    const [editedData, setEditedData] = useState([]);
    const [loading, setLoading] = useState(false)

    const totalPaid = billingData?.billing_data?.filter((item) => item?.status === "paid");
    const totalAmount = totalPaid?.reduce((sum, item) => sum + Number(item.amount || 0), 0);

    const totalUnpaid = billingData?.billing_data?.filter((item) => item?.status === "unpaid");
    const unpaidAmount = totalUnpaid?.reduce((sum, item) => sum + Number(item.amount || 0), 0);

    const totalError = billingData?.billing_data?.filter((item) => item?.error_code !== null)?.length || 0;

    const handleEdit = () => {
        setEditedData(JSON.parse(JSON.stringify(billingData.billing_data)));
        setIsEditing(true);
    };

    const handleCancel = () => {
        setIsEditing(false);
        setEditedData([]);
    };

    const handleSave = () => {
        setLoading(true)
        const updates = editedData.map(item => ({
            id: item.id,
            amount: Number(item.amount),
        }));

        const payload = { updates, billing_id: id };
        dispatch(updateHistory(payload)).then((res) => {
            if (res?.type === "update-history/fulfilled") {
                toast.success(res?.payload?.message)
                setLoading(false)
                navigate('/dashboard/billinglisting')
            } else {
                toast.error("Error occured")
                setLoading(false)
            }
        })
        setIsEditing(false);
    };

    const handleInputChange = (index, key, value) => {
        const updated = [...editedData];
        updated[index][key] = value;
        setEditedData(updated);
    };

    return (
        <div className="p-2 space-y-8">
            {loading && <Loader />}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 text-lg font-semibold">
                <button
                    onClick={() => navigate("/dashboard/billinglisting")}
                    className="px-3 py-2 rounded-full hover:bg-gray-200 transition w-max"
                >
                    <FontAwesomeIcon icon={faArrowLeft} className="mr-2" />
                    Back
                </button>
                <div className="text-indigo-700 flex items-center gap-2">
                    <FontAwesomeIcon icon={faCalendarAlt} />
                    <span>
                        Billing Summary for {billingData?.month} {billingData?.year}
                    </span>
                </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="flex items-center p-5 bg-green-100 border-l-4 border-green-600 rounded-xl shadow">
                    <FontAwesomeIcon icon={faMoneyBillWave} className="text-green-700 text-3xl mr-4" />
                    <div>
                        <p className="text-sm font-semibold text-green-700">Total Paid</p>
                        <p className="text-xl font-bold text-green-800">$ {totalAmount || 0}</p>
                    </div>
                </div>
                <div className="flex items-center p-5 bg-yellow-100 border-l-4 border-yellow-600 rounded-xl shadow">
                    <FontAwesomeIcon icon={faMoneyCheckDollar} className="text-yellow-700 text-3xl mr-4" />
                    <div>
                        <p className="text-sm font-semibold text-yellow-700">Total Unpaid</p>
                        <p className="text-xl font-bold text-yellow-800">$ {unpaidAmount}</p>
                    </div>
                </div>
                <div className="flex items-center p-5 bg-red-100 border-l-4 border-red-600 rounded-xl shadow">
                    <FontAwesomeIcon icon={faTriangleExclamation} className="text-red-700 text-3xl mr-4" />
                    <div>
                        <p className="text-sm font-semibold text-red-700">EXPL Codes</p>
                        <p className="text-xl font-bold text-red-800">{totalError}</p>
                    </div>
                </div>
            </div>
            <div className="bg-white shadow rounded-xl overflow-x-auto">
                <div className="px-6 py-4 border-b bg-gray-50 flex justify-between items-center">
                    <h3 className="text-lg font-semibold text-gray-800">Error Records</h3>
                    {!isEditing ? (
                        <button
                            onClick={handleEdit}
                            className="flex items-center gap-2 bg-indigo-600 text-white px-4 py-1 rounded-full text-sm hover:bg-indigo-700 transition shadow-sm"
                        >
                            <FontAwesomeIcon icon={faEdit} />
                            Edit
                        </button>
                    ) : (
                        <div className="flex gap-2">
                            <button
                                onClick={handleSave}
                                className="flex items-center gap-2 bg-green-600 text-white px-4 py-1 rounded-full text-sm hover:bg-green-700 transition shadow-sm"
                            >
                                <FontAwesomeIcon icon={faCheck} />
                                Update
                            </button>
                            <button
                                onClick={handleCancel}
                                className="flex items-center gap-2 bg-red-500 text-white px-4 py-1 rounded-full text-sm hover:bg-red-600 transition shadow-sm"
                            >
                                <FontAwesomeIcon icon={faTimes} />
                                Cancel
                            </button>
                        </div>
                    )}
                </div>
                <table className="min-w-full divide-y divide-gray-200 text-sm text-left">
                    <thead className="bg-gray-100 text-gray-600 uppercase text-xs">
                        <tr>
                            {[
                                "Name", "Plan", "Year", "Month", "Provider", "Type",
                                "Status", "Confirmation", "EXPL Code", "Notes", "Amount"
                            ].map((header, i) => (
                                <th key={i} className="px-6 py-3 whitespace-nowrap">{header}</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                        {(isEditing ? editedData : billingData.billing_data)?.map((item, index) => (
                            <tr key={index} className="hover:bg-gray-50">
                                <td className="px-3 py-3 text-gray-800 font-medium">
                                    {capitalizeFirstLetter(item?.members?.name)}
                                </td>
                                <td className="px-3 py-3 text-gray-700">{capitalizeFirstLetter(item?.plan)}</td>
                                <td className="px-3 py-3 text-gray-700">{billingData?.year}</td>
                                <td className="px-3 py-3 text-gray-700">{billingData?.month}</td>
                                <td className="px-3 py-3 text-gray-700">{billingData?.provider?.name}</td>
                                <td className="px-3 py-3 text-gray-700">{billingData?.type?.name}</td>
                                <td className="px-3 py-3 text-center">
                                    <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold
                                            ${item?.status === "paid"
                                            ? "text-green-700 bg-green-100"
                                            : "text-red-700 bg-red-100"}`}>
                                        {item?.status}
                                    </span>
                                </td>
                                {["confirmation_id", "error_code", "notes", "amount"].map((key) => (
                                    <td key={key} className="px-3 py-3 text-gray-700">
                                        {key === "amount" && isEditing ? (
                                            <input
                                                type="number"
                                                className="border px-2 py-1 rounded text-sm w-full"
                                                value={item[key] ?? ""}
                                                onChange={(e) =>
                                                    handleInputChange(index, key, e.target.value)
                                                }
                                            />
                                        ) : (
                                            <span className={key === "amount" ? "font-bold text-gray-800" : ""}>
                                                {item?.[key] || (key === "amount" ? "$ 0" : "--")}
                                            </span>
                                        )}
                                    </td>
                                ))}

                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default BillingHistory;
